import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
const SESSION_KEY = 'fidele_participant';

const STATUS_COLORS = {
  nouveau: '#000000', membre: '#000000', serviteur: '#000000', invite: '#000000',
};
const STATUS_LABELS = {
  nouveau: 'Nouveau', membre: 'Membre régulier', serviteur: 'Serviteur', invite: 'Invité',
};

export default function ScanPresence() {
  const [searchParams] = useSearchParams();
  const activityQr = searchParams.get('activity');

  // Activité chargée depuis l'API
  const [activity, setActivity] = useState(null);
  const [activityError, setActivityError] = useState('');

  // Participant (depuis session ou saisie code)
  const [participant, setParticipant] = useState(null);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [codeLoading, setCodeLoading] = useState(false);

  // Résultat du scan
  const [result, setResult] = useState(null); // { success, already_recorded, message, ... }
  const [scanning, setScanning] = useState(false);

  // ── Charger l'activité depuis le QR ──────────────────────────────────────
  useEffect(() => {
    if (!activityQr) { setActivityError('QR invalide — aucune activité trouvée.'); return; }
    axios.get(`${API}/activities/qr/${activityQr}/`)
      .then(r => setActivity(r.data))
      .catch(() => setActivityError("Activité introuvable. Ce QR n'est peut-être plus valide."));
  }, [activityQr]);

  // ── Récupérer le participant depuis la session (déjà connecté au portail) ─
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_KEY);
      if (saved) setParticipant(JSON.parse(saved));
    } catch { /* */ }
  }, []);

  // ── Connexion via code membre ─────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setCodeLoading(true);
    setCodeError('');
    try {
      const res = await axios.post(`${API}/participants/fidele/login/`, { member_code: code.trim() });
      setParticipant(res.data);
    } catch {
      setCodeError('Code membre introuvable. Vérifiez votre code EGL-XXXX.');
    } finally {
      setCodeLoading(false);
    }
  };

  // ── Enregistrer la présence ───────────────────────────────────────────────
  const handleScan = async () => {
    if (!participant || !activity) return;
    setScanning(true);
    try {
      const res = await axios.post(`${API}/attendance/scan-qr/`, {
        activity_qr: activityQr,
        participant_qr: participant.qr_code,
      });
      setResult(res.data);
    } catch (err) {
      const msg = err.response?.data?.error || 'Erreur lors de l\'enregistrement.';
      setResult({ success: false, error: true, message: msg });
    } finally {
      setScanning(false);
    }
  };

  const color = participant ? (STATUS_COLORS[participant.status] || '#000000') : '#000000';
  const initials = participant
    ? `${participant.first_name?.[0] || ''}${participant.last_name?.[0] || ''}`.toUpperCase()
    : '';

  // ── UI ────────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f4ff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>

        {/* Logo / En-tête */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '16px', backgroundColor: '#1e40af', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
          </div>
          <h1 style={{ fontSize: '1.3rem', fontWeight: 900, color: '#1e293b', margin: 0 }}>Marquer ma présence</h1>
          <p style={{ fontSize: '0.82rem', color: '#64748b', marginTop: '4px' }}>Confirmation de présence à l'activité</p>
        </div>

        {/* Erreur activité */}
        {activityError && (
          <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '12px', padding: '16px', textAlign: 'center', color: '#b91c1c', fontWeight: 600, fontSize: '0.9rem' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'block', margin: '0 auto 8px' }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {activityError}
          </div>
        )}

        {/* Carte activité */}
        {activity && (
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '18px', border: '1px solid #e2e8f0', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '10px', backgroundColor: `${activity.activity_type_color || '#1e40af'}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={activity.activity_type_color || '#1e40af'} strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: '1rem', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{activity.name}</div>
                <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>
                  {activity.date} · {activity.start_time?.slice(0, 5)}{activity.end_time ? ` - ${activity.end_time.slice(0, 5)}` : ''}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{activity.location}</div>
              </div>
              {/* Statut activité */}
              <div style={{ flexShrink: 0 }}>
                <span style={{
                  fontSize: '0.65rem', fontWeight: 700, padding: '3px 8px', borderRadius: '999px',
                  backgroundColor: activity.status === 'ongoing' ? '#d1fae5' : '#eff6ff',
                  color: activity.status === 'ongoing' ? '#065f46' : '#1e40af',
                }}>
                  {activity.status === 'ongoing' ? 'En cours' : activity.status === 'planned' ? 'Planifiée' : activity.status === 'completed' ? 'Terminée' : 'Annulée'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Résultat du scan */}
        {result && (
          <div style={{
            backgroundColor: result.already_recorded ? '#fffbeb' : result.error ? '#fef2f2' : '#f0fdf4',
            border: `1px solid ${result.already_recorded ? '#fde68a' : result.error ? '#fecaca' : '#bbf7d0'}`,
            borderRadius: '16px', padding: '24px', textAlign: 'center', marginBottom: '16px',
          }}>
            <div style={{ width: '52px', height: '52px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', backgroundColor: result.already_recorded ? '#fef9c3' : result.error ? '#fee2e2' : '#dcfce7' }}>
              {result.already_recorded && <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>}
              {result.error && <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>}
              {!result.already_recorded && !result.error && <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>}
            </div>
            <div style={{ fontWeight: 800, fontSize: '1.1rem', color: result.already_recorded ? '#92400e' : result.error ? '#b91c1c' : '#166534', marginBottom: '6px' }}>
              {result.already_recorded ? 'Déjà enregistré' : result.error ? 'Erreur' : 'Présence confirmée !'}
            </div>
            <div style={{ fontSize: '0.85rem', color: '#475569', marginBottom: result.success ? '12px' : 0 }}>
              {result.message}
            </div>
            {result.success && (
              <div style={{ fontSize: '0.78rem', color: '#64748b', backgroundColor: 'white', borderRadius: '8px', padding: '8px 12px', display: 'inline-flex', alignItems: 'center', gap: '5px', marginTop: '4px' }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                {result.recorded_at_display}
              </div>
            )}
          </div>
        )}

        {/* Identification */}
        {activity && !result && (
          <>
            {participant ? (
              /* Participant identifié */
              <div style={{ backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden', marginBottom: '16px' }}>
                <div style={{ padding: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '52px', height: '52px', borderRadius: '50%', backgroundColor: `${color}18`, border: `2px solid ${color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
                    {participant.photo_url
                      ? <img src={participant.photo_url} alt="profil" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <span style={{ fontWeight: 900, fontSize: '1.1rem', color }}>{initials}</span>
                    }
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: '1rem', color: '#1e293b' }}>{participant.first_name} {participant.last_name}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '3px' }}>
                      <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: color }} />
                      <span style={{ fontSize: '0.75rem', fontWeight: 600, color }}>{STATUS_LABELS[participant.status]}</span>
                      <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>· {participant.member_code}</span>
                    </div>
                  </div>
                  <button onClick={() => setParticipant(null)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '0.75rem', padding: '4px' }}>
                    Changer
                  </button>
                </div>
                <div style={{ padding: '0 16px 16px' }}>
                  <button
                    onClick={handleScan}
                    disabled={scanning}
                    style={{ width: '100%', padding: '14px', backgroundColor: color, color: 'white', border: 'none', borderRadius: '10px', cursor: scanning ? 'not-allowed' : 'pointer', fontWeight: 800, fontSize: '1rem', opacity: scanning ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    {scanning ? (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                        Confirmer ma présence
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              /* Saisir le code membre */
              <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: '16px' }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b', marginBottom: '4px' }}>Identifiez-vous</div>
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '16px' }}>Entrez votre code membre pour confirmer votre présence</div>
                <form onSubmit={handleLogin}>
                  <input
                    value={code}
                    onChange={e => setCode(e.target.value.toUpperCase())}
                    placeholder="EGL-0112"
                    style={{ width: '100%', padding: '12px 14px', border: `1.5px solid ${codeError ? '#fca5a5' : '#e2e8f0'}`, borderRadius: '10px', fontSize: '1rem', fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.08em', outline: 'none', boxSizing: 'border-box', marginBottom: codeError ? '6px' : '12px' }}
                  />
                  {codeError && <div style={{ fontSize: '0.78rem', color: '#b91c1c', marginBottom: '10px' }}>{codeError}</div>}
                  <button
                    type="submit"
                    disabled={codeLoading || !code.trim()}
                    style={{ width: '100%', padding: '13px', backgroundColor: '#1e40af', color: 'white', border: 'none', borderRadius: '10px', cursor: 'pointer', fontWeight: 800, fontSize: '0.95rem', opacity: codeLoading || !code.trim() ? 0.6 : 1 }}
                  >
                    {codeLoading ? 'Recherche...' : 'Continuer →'}
                  </button>
                </form>
              </div>
            )}
          </>
        )}

        {/* Recommencer */}
        {result && (
          <button
            onClick={() => { setResult(null); }}
            style={{ width: '100%', padding: '12px', backgroundColor: 'white', color: '#1e40af', border: '1.5px solid #bfdbfe', borderRadius: '10px', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem' }}
          >
            ← Retour
          </button>
        )}

        <p style={{ textAlign: 'center', fontSize: '0.72rem', color: '#cbd5e1', marginTop: '24px' }}>
          Portail présences · Sécurisé
        </p>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
